import _ from 'lodash';
import { getIntl } from 'umi';

import env from '@/config/env';
import { CoreEnums } from '@/utils/domain/constants';
import { fileSizeDisplay } from '@/utils/infrastructure/file-system';
import BaseFieldHandler from '../BaseFieldHandler';
import { preloadAttachmentFiles } from '../utils';

const { FormFieldType, FormFieldStringType } = CoreEnums;

export default class AttachmentFieldHandler extends BaseFieldHandler {
  static supportedTypes = [
    FormFieldStringType.Image,
    FormFieldStringType.Photo,
    FormFieldStringType.Signature,
    FormFieldStringType.Attachment,
    FormFieldType.Image,
    FormFieldType.Photo,
    FormFieldType.Signature,
    FormFieldType.Attachment,
  ];

  handle() {
    const config = this.getBaseConfig();
    const { isDetails } = this.fieldState;

    const {
      type,
      value,
      allowMultiple,
      maxFileSize,
      minWidth,
      maxWidth,
      minHeight,
      maxHeight,
      maxAttachmentsAllowed,
      minAttachmentsRequired,
      allowedExtensions,
      formInstanceFieldAttachments,
      displayText,
      displayName,
      observations,
    } = this.baseConfig;

    const isImage = type === FormFieldType.Image || type === FormFieldStringType.Image;
    const isAttachment =
      type === FormFieldType.Attachment || type === FormFieldStringType.Attachment;
    const isSignature = type === FormFieldType.Signature || type === FormFieldStringType.Signature;
    const withPreloadFiles = (isImage || isAttachment) && config.value?.attachments?.length > 0;

    if (isAttachment && value && !_.has(value, 'attachments') && !isDetails) {
      delete config.value;
    }

    const fieldTypes = this.getFieldTypes();

    return {
      ...config,
      type: fieldTypes[type],
      multiple: !!allowMultiple,
      isDetail: isDetails,
      locale: false,
      maxFileSize,

      ...(maxFileSize && {
        help: [
          observations,
          getIntl().formatMessage(
            { id: allowMultiple ? 'form.help.maxFilesSize' : 'form.help.maxFileSize' },
            { maxFileSize: fileSizeDisplay(maxFileSize) },
          ),
        ]
          .filter(Boolean)
          .join(' \n'),
      }),

      ...(isImage && {
        allowedFormats: env.ALLOWED_IMAGE_EXTENSIONS,
        ...(minWidth > 1 && { minWidth }),
        ...(maxWidth > 1 && { maxWidth }),
        ...(minHeight > 1 && { minHeight }),
        ...(maxHeight > 1 && { maxHeight }),
      }),

      ...(!allowMultiple && { maxCount: 1 }),
      ...(!isSignature && { label: displayText ?? displayName }),
      ...(isAttachment && { formInstanceFieldAttachments }),
      ...(maxAttachmentsAllowed > 1 && { maxAttachmentsAllowed }),
      ...(minAttachmentsRequired > 1 && { minAttachmentsRequired }),
      // Details: keep empty media fields visible (muted chrome), do not hide.
      ...(allowedExtensions?.length > 0 && { allowedFormats: allowedExtensions }),
      ...(withPreloadFiles && {
        preloadedAttachmentFiles: preloadAttachmentFiles(config.value),
        isEditing: !isDetails,
      }),
    };
  }

  getFieldTypes() {
    return {
      [FormFieldStringType.Photo]: 'camera',
      [FormFieldStringType.Image]: 'upload-dragger-image',
      [FormFieldStringType.Signature]: 'canvas-draw',
      [FormFieldStringType.Attachment]: 'upload-dragger',
      [FormFieldType.Photo]: 'camera',
      [FormFieldType.Signature]: 'canvas-draw',
      [FormFieldType.Image]: 'upload-dragger-image',
      [FormFieldType.Attachment]: 'upload-dragger',
    };
  }
}
